import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { FadeIn, FadeInLeft, FadeInRight } from '../components/motion/FadeIn';
import { SectionLabel } from '../components/shared/SectionLabel';
import { CTASection } from '../components/shared/CTASection';
import {
  ArrowRight, DollarSign, TrendingUp, Calculator, Users,
  BarChart3, Building2, Target, CheckCircle2, Zap, ChevronDown, ChevronUp
} from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtDec = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n);

/* ═══ SHARED CALCULATION ENGINE ═══ */
function useCalc({ spend, costPerLead, bookPct, showRate, poorCreditPct, financingPct, declinedPct, aoxPct, archPrice, profitPct, archesPerCase, historyMonths, consultsReturnPct, declinedTakeCoursePct, leadsTakeCoursePct, improvePct, leadsReturnPct, sssCost, offices, ltnPct }) {
  return useMemo(() => {
    const lpm = spend / costPerLead;
    const pc = poorCreditPct/100; const bk = bookPct/100; const sh = showRate/100;
    const fi = financingPct/100; const dc = declinedPct/100; const ax = aoxPct/100;
    const pr = profitPct/100; const lc = leadsTakeCoursePct/100; const im = improvePct/100;
    const lr = leadsReturnPct/100; const cr = consultsReturnPct/100;
    const dcc = declinedTakeCoursePct/100; const lt = ltnPct/100;

    // LEADS
    const L = { leads: lpm, poor: lpm*pc };
    L.course = L.poor*lc; L.improve = L.course*im; L.ret = L.improve*lr;
    L.arch = L.ret*ax; L.arches = L.arch*archesPerCase;
    L.rev = L.arches*archPrice; L.profit = L.rev*pr - sssCost;
    L.annRev = L.rev*12; L.annProfit = L.profit*12;

    // CONSULTS
    const C = { booked: lpm*bk, showed: lpm*bk*sh };
    C.archC = C.showed*ax; C.finance = C.archC*fi; C.declined = C.finance*dc;
    C.course = C.declined*dcc; C.improve = C.course*im; C.ret = C.improve*cr;
    C.arches = C.ret*archesPerCase; C.rev = C.arches*archPrice;
    C.profit = C.rev*pr - sssCost; C.annRev = C.rev*12; C.annProfit = C.profit*12;

    // LTN
    const N = { leads: lpm*historyMonths, poor: lpm*historyMonths*pc };
    N.course = N.poor*lt; N.improve = N.course*im; N.ret = N.improve*lr;
    N.arch = N.ret*ax; N.arches = N.arch*archesPerCase;
    N.rev = N.arches*archPrice; N.profit = N.rev*pr - sssCost;

    // TOTALS
    const T = {};
    T.arches = L.arches + C.arches + N.arches;
    T.monthRev = L.rev + C.rev + N.rev;
    T.monthProfit = T.monthRev*pr - sssCost;
    T.annRev = T.monthRev*12; T.annProfit = T.monthProfit*12;

    // ALL OFFICES
    const A = {};
    A.monthRev = T.monthRev*offices; A.annRev = T.annRev*offices;
    A.monthProfit = T.monthProfit*offices; A.annProfit = T.annProfit*offices;
    A.sssCost = sssCost*offices*12;
    A.roi = A.sssCost > 0 ? A.annRev / A.sssCost : 0;

    return { L, C, N, T, A, lpm };
  }, [spend, costPerLead, bookPct, showRate, poorCreditPct, financingPct, declinedPct, aoxPct, archPrice, profitPct, archesPerCase, historyMonths, consultsReturnPct, declinedTakeCoursePct, leadsTakeCoursePct, improvePct, leadsReturnPct, sssCost, offices, ltnPct]);
}

/* ═══ HERO ═══ */
const HeroSection = () => (
  <section className="relative overflow-hidden pt-20 sm:pt-24 pb-8 sm:pb-10">
    <div className="absolute inset-0 -z-10" style={{ background: 'radial-gradient(1200px circle at 50% 10%, rgba(42,140,134,0.12), transparent 55%)' }} />
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8 text-center max-w-3xl">
      <FadeIn>
        <SectionLabel>ROI Calculator</SectionLabel>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">
          See Exactly How Much Revenue You're Leaving on the Table
        </h1>
        <p className="mt-5 text-base md:text-lg text-[hsl(var(--sss-ink))]/70">
          Adjust the sliders to match your practice. Watch the numbers speak for themselves.
        </p>
      </FadeIn>
    </div>
  </section>
);

/* ═══ SIMPLE CALCULATOR ═══ */
const SimpleCalculator = ({ inputs, setInputs, results }) => {
  const SI = ({ label, field, min, max, step, format }) => (
    <div>
      <div className="flex justify-between mb-2">
        <label className="text-sm font-medium text-[hsl(var(--sss-navy))]">{label}</label>
        <span className="text-sm font-bold text-[hsl(var(--sss-coral))]">
          {format === '$' ? fmt(inputs[field]) : format === '%' ? `${inputs[field]}%` : inputs[field]}
        </span>
      </div>
      <Slider value={[inputs[field]]} onValueChange={(v) => setInputs(p => ({...p, [field]: v[0]}))}
        min={min} max={max} step={step}
        className="[&_[role=slider]]:bg-[hsl(var(--sss-coral))] [&_[role=slider]]:border-[hsl(var(--sss-coral))]" />
      <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
        <span>{format === '$' ? fmt(min) : format === '%' ? `${min}%` : min}</span>
        <span>{format === '$' ? fmt(max) : format === '%' ? `${max}%` : max}</span>
      </div>
    </div>
  );

  return (
    <div className="grid gap-8 lg:grid-cols-12">
      <FadeInLeft className="lg:col-span-5">
        <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-md)] sticky top-20">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-6">
              <Calculator className="h-5 w-5 text-[hsl(var(--sss-teal))]" />
              <h3 className="text-lg font-semibold text-[hsl(var(--sss-navy))]">Your Practice Numbers</h3>
            </div>
            <div className="space-y-7">
              <SI label="Monthly Marketing Spend" field="spend" min={5000} max={100000} step={1000} format="$" />
              <SI label="Cost per Lead" field="costPerLead" min={10} max={300} step={1} format="$" />
              <SI label="Average Case Value" field="archPrice" min={3000} max={50000} step={1000} format="$" />
              <SI label="Profit Margin" field="profitPct" min={10} max={50} step={1} format="%" />
              <SI label="Number of Offices" field="offices" min={1} max={50} step={1} />
            </div>
          </CardContent>
        </Card>
      </FadeInLeft>

      <FadeInRight className="lg:col-span-7">
        <div className="space-y-6">
          {/* Big ROI */}
          <Card className="rounded-2xl border-2 border-[hsl(var(--sss-coral))]/30 bg-white shadow-[var(--shadow-lg)] overflow-hidden">
            <div className="bg-[hsl(var(--sss-navy))] px-6 py-8 sm:px-8 sm:py-10 text-center">
              <p className="text-sm text-white/60 mb-2">For every $1 you spend on Smile Score Solutions</p>
              <p className="text-5xl sm:text-7xl font-bold text-white">{fmt(results.A.roi)}</p>
              <p className="text-lg text-white/80 mt-2">comes back in recovered revenue</p>
            </div>
            <CardContent className="p-6 sm:p-8">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 rounded-xl bg-[hsl(var(--secondary))]">
                  <p className="text-2xl sm:text-3xl font-bold text-[hsl(var(--sss-navy))]">{fmt(results.A.annRev)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Annual Revenue Recovered</p>
                </div>
                <div className="text-center p-4 rounded-xl bg-[hsl(var(--secondary))]">
                  <p className="text-2xl sm:text-3xl font-bold text-[hsl(var(--sss-teal))]">{fmt(results.A.annProfit)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Annual Profit</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Breakdown */}
          <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)]">
            <CardContent className="p-6">
              <h4 className="text-base font-semibold text-[hsl(var(--sss-navy))] mb-4">Monthly Breakdown</h4>
              {[
                ['Leads Generated', `${fmtDec(results.lpm * inputs.offices)}/mo`, ''],
                ['Poor Credit Leads (65%)', `${fmtDec(results.lpm * 0.65 * inputs.offices)}/mo`, 'text-[hsl(var(--sss-coral))]'],
                ['Monthly Revenue Recovered', fmt(results.A.monthRev), 'text-[hsl(var(--sss-teal))] font-bold'],
                ['Monthly Profit', fmt(results.A.monthProfit), 'font-bold'],
                ['Smile Score Solutions Cost', `${fmt(inputs.sssCost * inputs.offices)}/mo`, 'text-muted-foreground'],
              ].map(([label, value, cls], i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-[hsl(var(--border))]/50 last:border-0">
                  <span className="text-sm text-[hsl(var(--sss-ink))]/70">{label}</span>
                  <span className={`text-sm font-semibold text-[hsl(var(--sss-navy))] ${cls}`}>{value}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* The Math */}
          <Card className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--accent))]/30 shadow-[var(--shadow-sm)]">
            <CardContent className="p-6">
              <h4 className="text-base font-semibold text-[hsl(var(--sss-navy))] mb-3">The Simple Math</h4>
              <p className="text-sm text-[hsl(var(--sss-ink))]/80">Your annual SSS investment: <span className="font-semibold">{fmt(results.A.sssCost)}</span></p>
              <p className="text-sm text-[hsl(var(--sss-ink))]/80">Revenue recovered annually: <span className="font-semibold text-[hsl(var(--sss-teal))]">{fmt(results.A.annRev)}</span></p>
              <p className="text-sm text-[hsl(var(--sss-ink))]/80 mt-1">That's a <span className="font-bold text-[hsl(var(--sss-navy))] text-lg">{fmtDec(results.A.roi)}x</span> return on your investment.</p>
              <p className="text-xs text-muted-foreground mt-2 italic">Even recovering just one additional case per year pays for the entire platform many times over.</p>
            </CardContent>
          </Card>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/contact" className="flex-1">
              <Button className="w-full bg-[hsl(var(--sss-coral))] hover:bg-[hsl(var(--sss-coral-2))] text-white rounded-xl h-12 text-base font-medium shadow-md">
                Book a Demo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href="https://approvemysmile.com/register/personal" target="_blank" rel="noopener noreferrer" className="flex-1">
              <Button variant="outline" className="w-full border-[hsl(var(--sss-navy))] text-[hsl(var(--sss-navy))] hover:bg-[hsl(var(--sss-navy))] hover:text-white rounded-xl h-12 text-base font-medium">
                Get Started Now
              </Button>
            </a>
          </div>
        </div>
      </FadeInRight>
    </div>
  );
};

/* ═══ EXPERT CALCULATOR ═══ */
const ExpertCalculator = ({ inputs, setInputs, results }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const SI = ({ label, field, min, max, step, format }) => (
    <div>
      <div className="flex justify-between mb-1.5">
        <label className="text-xs font-medium text-[hsl(var(--sss-navy))]">{label}</label>
        <span className="text-xs font-bold text-[hsl(var(--sss-coral))]">
          {format === '$' ? fmt(inputs[field]) : format === '%' ? `${inputs[field]}%` : `${inputs[field]}${format || ''}`}
        </span>
      </div>
      <Slider value={[inputs[field]]} onValueChange={(v) => setInputs(p => ({...p, [field]: v[0]}))}
        min={min} max={max} step={step}
        className="[&_[role=slider]]:bg-[hsl(var(--sss-teal))] [&_[role=slider]]:border-[hsl(var(--sss-teal))] [&_[role=slider]]:h-4 [&_[role=slider]]:w-4" />
    </div>
  );

  const Row = ({ label, value, hl }) => (
    <div className={`flex justify-between items-center py-1.5 ${hl ? '' : 'border-b border-[hsl(var(--border))]/50'}`}>
      <span className="text-xs text-[hsl(var(--sss-ink))]/60">{label}</span>
      <span className={`text-xs font-semibold ${hl ? 'text-[hsl(var(--sss-coral))] text-sm' : 'text-[hsl(var(--sss-navy))]'}`}>{value}</span>
    </div>
  );

  return (
    <>
      {/* ROI Hero Bar */}
      <Card className="rounded-2xl border-2 border-[hsl(var(--sss-coral))]/20 bg-[hsl(var(--sss-navy))] shadow-[var(--shadow-lg)] mb-8 overflow-hidden">
        <CardContent className="p-6 sm:p-8">
          <div className="grid gap-6 sm:grid-cols-4 text-center text-white">
            <div>
              <p className="text-xs text-white/50 mb-1">For every $1 in SSS</p>
              <p className="text-3xl sm:text-4xl font-bold">{fmt(results.A.roi)}</p>
              <p className="text-xs text-white/60">comes back</p>
            </div>
            <div>
              <p className="text-xs text-white/50 mb-1">Monthly Revenue</p>
              <p className="text-2xl sm:text-3xl font-bold text-[hsl(var(--sss-coral))]">{fmt(results.A.monthRev)}</p>
              <p className="text-xs text-white/60">recovered</p>
            </div>
            <div>
              <p className="text-xs text-white/50 mb-1">Annual Revenue</p>
              <p className="text-2xl sm:text-3xl font-bold">{fmt(results.A.annRev)}</p>
              <p className="text-xs text-white/60">recovered</p>
            </div>
            <div>
              <p className="text-xs text-white/50 mb-1">Annual Profit</p>
              <p className="text-2xl sm:text-3xl font-bold text-[hsl(168,76%,55%)]">{fmt(results.A.annProfit)}</p>
              <p className="text-xs text-white/60">after SSS cost</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-8 lg:grid-cols-12">
        {/* Inputs */}
        <FadeInLeft className="lg:col-span-4">
          <div className="sticky top-20 space-y-4">
            <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-md)]">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-5">
                  <Calculator className="h-4 w-4 text-[hsl(var(--sss-teal))]" />
                  <h3 className="text-sm font-semibold text-[hsl(var(--sss-navy))]">Your Practice Numbers</h3>
                </div>
                <div className="space-y-5">
                  <SI label="Monthly Marketing Spend" field="spend" min={5000} max={100000} step={1000} format="$" />
                  <SI label="Cost per Lead" field="costPerLead" min={10} max={300} step={1} format="$" />
                  <SI label="Average Case Value" field="archPrice" min={3000} max={50000} step={1000} format="$" />
                  <SI label="Profit Margin" field="profitPct" min={10} max={60} step={1} format="%" />
                  <SI label="Arches per Case" field="archesPerCase" min={1} max={3} step={0.5} />
                  <SI label="Number of Offices" field="offices" min={1} max={50} step={1} />
                  <SI label="Months of Marketing History" field="historyMonths" min={3} max={60} step={1} format=" mo" />
                </div>
              </CardContent>
            </Card>
            <button onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full flex items-center justify-center gap-2 text-xs font-medium text-muted-foreground hover:text-[hsl(var(--sss-navy))] transition-colors py-2">
              {showAdvanced ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {showAdvanced ? 'Hide' : 'Show'} Conversion Assumptions
            </button>
            {showAdvanced && (
              <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)]">
                <CardContent className="p-5 space-y-4">
                  <SI label="% Leads w/ Poor Credit" field="poorCreditPct" min={30} max={80} step={1} format="%" />
                  <SI label="Booking Rate" field="bookPct" min={5} max={40} step={1} format="%" />
                  <SI label="Show Rate" field="showRate" min={10} max={80} step={1} format="%" />
                  <SI label="% Need Financing" field="financingPct" min={40} max={90} step={1} format="%" />
                  <SI label="% Declined" field="declinedPct" min={20} max={70} step={1} format="%" />
                  <SI label="Case Acceptance (AOX)" field="aoxPct" min={40} max={90} step={1} format="%" />
                  <Separator />
                  <SI label="% Leads Take Course" field="leadsTakeCoursePct" min={1} max={20} step={1} format="%" />
                  <SI label="% Declined Consults Take Course" field="declinedTakeCoursePct" min={10} max={60} step={1} format="%" />
                  <SI label="% Improve Credit" field="improvePct" min={5} max={30} step={1} format="%" />
                  <SI label="% Leads Return" field="leadsReturnPct" min={10} max={50} step={1} format="%" />
                  <SI label="% Consults Return" field="consultsReturnPct" min={30} max={80} step={1} format="%" />
                  <SI label="% Old Leads Take Course" field="ltnPct" min={1} max={10} step={1} format="%" />
                </CardContent>
              </Card>
            )}
          </div>
        </FadeInLeft>

        {/* 3 Paths */}
        <FadeInRight className="lg:col-span-8 space-y-5">
          {/* PATH 1 */}
          <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)]">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-6 w-6 rounded-md bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">1</div>
                <h4 className="text-sm font-semibold text-[hsl(var(--sss-navy))]">From Marketing Leads</h4>
                <Badge className="bg-blue-50 text-blue-700 text-[10px] ml-auto">New Leads</Badge>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Row label="Marketing Leads / month" value={fmtDec(results.L.leads)} />
                  <Row label="Poor Credit Leads" value={fmtDec(results.L.poor)} />
                  <Row label="Take the Course" value={fmtDec(results.L.course)} />
                  <Row label="Credit Improves" value={fmtDec(results.L.improve)} />
                  <Row label="Return to Office" value={fmtDec(results.L.ret)} />
                </div>
                <div>
                  <Row label="Arch Patients" value={fmtDec(results.L.arch)} />
                  <Row label="Arches" value={fmtDec(results.L.arches)} />
                  <Row label="Monthly Revenue" value={fmt(results.L.rev)} hl />
                  <Row label="Annual Revenue" value={fmt(results.L.annRev)} />
                  <Row label="Annual Profit" value={fmt(results.L.annProfit)} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* PATH 2 */}
          <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)]">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-6 w-6 rounded-md bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">2</div>
                <h4 className="text-sm font-semibold text-[hsl(var(--sss-navy))]">From Declined Consults</h4>
                <Badge className="bg-emerald-50 text-emerald-700 text-[10px] ml-auto">Patients Who Showed Up</Badge>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Row label="Booked Consults" value={fmtDec(results.C.booked)} />
                  <Row label="Consults Showed" value={fmtDec(results.C.showed)} />
                  <Row label="Arch Consults (AOX)" value={fmtDec(results.C.archC)} />
                  <Row label="Need Financing" value={fmtDec(results.C.finance)} />
                  <Row label="Declined" value={fmtDec(results.C.declined)} />
                </div>
                <div>
                  <Row label="Take the Course" value={fmtDec(results.C.course)} />
                  <Row label="Credit Improves" value={fmtDec(results.C.improve)} />
                  <Row label="Return for Treatment" value={fmtDec(results.C.ret)} />
                  <Row label="Monthly Revenue" value={fmt(results.C.rev)} hl />
                  <Row label="Annual Revenue" value={fmt(results.C.annRev)} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* PATH 3 */}
          <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)]">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-6 w-6 rounded-md bg-amber-100 text-amber-600 flex items-center justify-center text-xs font-bold">3</div>
                <h4 className="text-sm font-semibold text-[hsl(var(--sss-navy))]">From Long-Term Nurturing</h4>
                <Badge className="bg-amber-50 text-amber-700 text-[10px] ml-auto">Old Leads</Badge>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Row label={`Historical Leads (${inputs.historyMonths} mo)`} value={fmtDec(results.N.leads)} />
                  <Row label="Poor Credit Leads" value={fmtDec(results.N.poor)} />
                  <Row label="Take the Course" value={fmtDec(results.N.course)} />
                  <Row label="Credit Improves" value={fmtDec(results.N.improve)} />
                </div>
                <div>
                  <Row label="Return to Office" value={fmtDec(results.N.ret)} />
                  <Row label="Arches" value={fmtDec(results.N.arches)} />
                  <Row label="Monthly Revenue" value={fmt(results.N.rev)} hl />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* TOTAL */}
          <Card className="rounded-2xl border-2 border-[hsl(var(--sss-teal))]/30 bg-white shadow-[var(--shadow-md)]">
            <CardContent className="p-5">
              <h4 className="text-base font-bold text-[hsl(var(--sss-navy))] mb-4">Combined Total {inputs.offices > 1 ? `(${inputs.offices} offices)` : '(per office)'}</h4>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  ['Arches / month', fmtDec(results.T.arches * inputs.offices), 'sss-navy'],
                  ['Monthly Revenue', fmt(results.A.monthRev), 'sss-coral'],
                  ['Annual Revenue', fmt(results.A.annRev), 'sss-navy'],
                  ['Annual Profit', fmt(results.A.annProfit), 'sss-teal'],
                ].map(([l,v,c],i) => (
                  <div key={i} className="rounded-xl bg-[hsl(var(--secondary))] p-3 text-center">
                    <p className="text-xs text-muted-foreground">{l}</p>
                    <p className={`text-lg font-bold text-[hsl(var(--${c}))]`}>{v}</p>
                  </div>
                ))}
              </div>
              <Separator className="my-4" />
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-center sm:text-left">
                <div><p className="text-xs text-muted-foreground">SSS investment</p><p className="text-sm font-medium">{fmt(results.A.sssCost)} / year</p></div>
                <div className="h-8 w-px bg-[hsl(var(--border))] hidden sm:block" />
                <div><p className="text-xs text-muted-foreground">Revenue recovered</p><p className="text-sm font-bold text-[hsl(var(--sss-coral))]">{fmt(results.A.annRev)} / year</p></div>
                <div className="h-8 w-px bg-[hsl(var(--border))] hidden sm:block" />
                <div><p className="text-xs text-muted-foreground">ROI</p><p className="text-2xl font-bold text-[hsl(var(--sss-navy))]">{fmtDec(results.A.roi)}x</p></div>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Link to="/contact" className="flex-1">
              <Button className="w-full bg-[hsl(var(--sss-coral))] hover:bg-[hsl(var(--sss-coral-2))] text-white rounded-xl h-12 text-base font-medium shadow-md">
                Book a Demo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href="https://approvemysmile.com/register/personal" target="_blank" rel="noopener noreferrer" className="flex-1">
              <Button variant="outline" className="w-full border-[hsl(var(--sss-navy))] text-[hsl(var(--sss-navy))] hover:bg-[hsl(var(--sss-navy))] hover:text-white rounded-xl h-12 text-base font-medium">Get Started Now</Button>
            </a>
          </div>
        </FadeInRight>
      </div>
    </>
  );
};

/* ═══ MAIN PAGE ═══ */
export default function CalculatorPage() {
  const [mode, setMode] = useState('simple');
  const [inputs, setInputs] = useState({
    spend: 20000, costPerLead: 50, bookPct: 15, showRate: 20,
    poorCreditPct: 65, financingPct: 75, declinedPct: 50, aoxPct: 70,
    archPrice: 20000, profitPct: 20, archesPerCase: 1.5, historyMonths: 24,
    consultsReturnPct: 60, declinedTakeCoursePct: 35, leadsTakeCoursePct: 5,
    improvePct: 10, leadsReturnPct: 20, sssCost: 249, offices: 1, ltnPct: 1,
  });

  const results = useCalc(inputs);

  return (
    <div>
      <HeroSection />

      {/* Toggle */}
      <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8 mb-8">
        <FadeIn>
          <div className="flex justify-center">
            <div className="inline-flex rounded-xl border border-[hsl(var(--border))] bg-white p-1 shadow-[var(--shadow-sm)]">
              <button
                onClick={() => setMode('simple')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  mode === 'simple'
                    ? 'bg-[hsl(var(--sss-navy))] text-white shadow-sm'
                    : 'text-[hsl(var(--sss-navy))]/60 hover:text-[hsl(var(--sss-navy))]'
                }`}
              >
                Simple Calculator
              </button>
              <button
                onClick={() => setMode('expert')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  mode === 'expert'
                    ? 'bg-[hsl(var(--sss-navy))] text-white shadow-sm'
                    : 'text-[hsl(var(--sss-navy))]/60 hover:text-[hsl(var(--sss-navy))]'
                }`}
              >
                Expert Calculator
              </button>
            </div>
          </div>
        </FadeIn>
      </div>

      {/* Calculator */}
      <section className="pb-8 sm:pb-12">
        <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
          {mode === 'simple'
            ? <SimpleCalculator inputs={inputs} setInputs={setInputs} results={results} />
            : <ExpertCalculator inputs={inputs} setInputs={setInputs} results={results} />
          }
        </div>
      </section>

      <div className="py-6 bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
          <p className="text-[10px] sm:text-xs text-muted-foreground text-center max-w-3xl mx-auto">
            This calculator uses conservative industry benchmarks. Default conversion assumptions are based on dental industry data. Individual results will vary based on practice, patient engagement, and financial circumstances. No specific outcome is guaranteed. Switch to Expert mode and use "Show Conversion Assumptions" to adjust all rates.
          </p>
        </div>
      </div>

      <CTASection
        headline="The Numbers Don't Lie"
        subheadline="Your practice is already paying to get these patients through the door. Stop letting them walk out. It's a no-brainer."
      />
    </div>
  );
}
